import { seoContent, SITE_URL } from '../content/seo';

const DEFAULT_LOCALE = 'zh-CN';
const TELEGRAM_URL = 'https://t.me/socs_sup366';

function localeContent(locale) {
  return seoContent[locale] || seoContent[DEFAULT_LOCALE];
}

function pageContent(routeName, locale) {
  const content = localeContent(locale);
  return content.pages[routeName] || null;
}

function canonicalUrl(path) {
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

function upsertMeta(attribute, key, content) {
  if (!content) {
    return;
  }

  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function upsertLink(rel, href, extraAttributes = {}) {
  if (!href) {
    return;
  }

  const selector = extraAttributes.hreflang
    ? `link[rel="${rel}"][hreflang="${extraAttributes.hreflang}"]`
    : `link[rel="${rel}"]`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
  Object.entries(extraAttributes).forEach(([name, value]) => {
    element.setAttribute(name, value);
  });
}

function upsertStructuredData(json) {
  let element = document.head.querySelector('script[data-seo="structured-data"]');
  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.setAttribute('data-seo', 'structured-data');
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(json);
}

function buildBreadcrumb(locale, page, route) {
  const content = localeContent(locale);
  const currentTitle = page?.title || content.defaultTitle;
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: content.siteName,
      item: `${SITE_URL}/`,
    },
  ];

  if (route.path !== '/') {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: currentTitle,
      item: canonicalUrl(page?.canonicalPath || route.path),
    });
  }

  return {
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

function buildPageEntity(locale, page, route) {
  const title = page?.title || localeContent(locale).defaultTitle;
  const description = page?.description || localeContent(locale).defaultDescription;
  const url = canonicalUrl(page?.canonicalPath || route.path);

  if (route.name === 'Home') {
    return [
      {
        '@type': 'SoftwareApplication',
        name: title,
        applicationCategory: 'DeveloperApplication',
        operatingSystem: 'Web',
        inLanguage: [locale, locale === 'zh-CN' ? 'en-US' : 'zh-CN'],
        description,
        url,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: locale === 'zh-CN'
          ? [
              {
                '@type': 'Question',
                name: 'Cloudflare Tools 可以做什么？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '可用于管理 Cloudflare DNS、SSL/TLS、安全规则、国家访问限制、Workers 与 Pages，并支持会员 API 自动化。',
                },
              },
              {
                '@type': 'Question',
                name: '是否支持中文用户直接在网页里操作？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '支持，普通用户无需注册即可直接使用网页控制台，VIP 会员可再启用 API Key 和加密 Token。',
                },
              },
            ]
          : [
              {
                '@type': 'Question',
                name: 'What can Cloudflare Tools do?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'It helps manage Cloudflare DNS, SSL/TLS, security rules, country access control, Workers, Pages, and member API automation.',
                },
              },
              {
                '@type': 'Question',
                name: 'Can users operate it directly in the browser?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. Public users can use the browser-based console directly, and VIP members can add API keys and encrypted tokens.',
                },
              },
            ],
      },
    ];
  }

  if (route.name === 'Docs') {
    return {
      '@type': 'TechArticle',
      headline: title,
      description,
      url,
      inLanguage: locale,
      about: ['Cloudflare API', 'DNS', 'SSL/TLS', 'Security rules', 'Workers', 'Pages'],
    };
  }

  if (route.name === 'Pricing') {
    return {
      '@type': 'Service',
      name: title,
      description,
      url,
      provider: {
        '@type': 'Organization',
        name: localeContent(locale).siteName,
      },
    };
  }

  return {
    '@type': 'WebPage',
    name: title,
    description,
    url,
    inLanguage: locale,
  };
}

function buildStructuredData(locale, page, route) {
  const content = localeContent(locale);
  const pageUrl = canonicalUrl(page?.canonicalPath || route.path);
  const pageEntity = buildPageEntity(locale, page, route);

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: content.siteName,
        url: `${SITE_URL}/`,
        description: content.organizationDescription,
        sameAs: [TELEGRAM_URL],
      },
      {
        '@type': 'WebSite',
        name: content.siteName,
        url: `${SITE_URL}/`,
        inLanguage: [locale, locale === 'zh-CN' ? 'en-US' : 'zh-CN'],
        description: content.defaultDescription,
      },
      {
        '@type': 'WebPage',
        name: page?.title || content.defaultTitle,
        description: page?.description || content.defaultDescription,
        url: pageUrl,
        inLanguage: locale,
        isPartOf: {
          '@type': 'WebSite',
          name: content.siteName,
          url: `${SITE_URL}/`,
        },
      },
      buildBreadcrumb(locale, page, route),
      ...(Array.isArray(pageEntity) ? pageEntity : [pageEntity]),
    ],
  };
}

export function applySeo(route, locale = DEFAULT_LOCALE) {
  const content = localeContent(locale);
  const page = pageContent(route.name, locale);
  const title = page?.title || content.defaultTitle;
  const description = page?.description || content.defaultDescription;
  const keywords = page?.keywords || content.defaultKeywords;
  const robots = page?.robots || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
  const url = canonicalUrl(page?.canonicalPath || route.path);
  const ogLocale = locale === 'zh-CN' ? 'zh_CN' : 'en_US';

  document.title = title;
  document.documentElement.lang = locale;

  upsertMeta('name', 'description', description);
  upsertMeta('name', 'keywords', keywords);
  upsertMeta('name', 'robots', robots);
  upsertMeta('name', 'author', content.siteName);
  upsertMeta('name', 'application-name', content.siteName);
  upsertMeta('name', 'apple-mobile-web-app-title', content.siteName);

  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:site_name', content.siteName);
  upsertMeta('property', 'og:locale', ogLocale);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', url);

  upsertMeta('name', 'twitter:card', 'summary');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);

  upsertLink('canonical', url);
  upsertLink('alternate', url, { hreflang: locale });
  upsertLink('alternate', canonicalUrl(page?.canonicalPath || route.path), {
    hreflang: locale === 'zh-CN' ? 'en-US' : 'zh-CN',
  });
  upsertLink('alternate', url, { hreflang: 'x-default' });

  upsertStructuredData(buildStructuredData(locale, page, route));
}
